import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function StockMovementView() {
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [movements, setMovements] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const userRole = userResponse.data.role;
        setOrganizationId(userResponse.data.organization?._id);

        if (userRole === "Main Admin") {
          fetchAllLocations();
        } else {
          fetchFilteredLocations();
        }

      } catch (error) {
        console.error("Error fetching user role:", error);
        toast.error("Error fetching user role.");
      }
    };

    fetchUserRole();
  }, []);

  const fetchAllLocations = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const formatted = response.data.map((loc) => ({
        value: loc._id,
        label: loc.location_name,
      }));

      setLocations([{ value: "all", label: "Select All" }, ...formatted]);
      if (formatted.length === 1) setSelectedLocation(formatted[0]);
    } catch (error) {
      toast.error("Error fetching locations.");
    }
  };

  const fetchFilteredLocations = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/products/filtered-locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const formatted = response.data.locations.map((loc) => ({
        value: loc._id,
        label: loc.location_name,
      }));

      setLocations([{ value: "all", label: "Select All" }, ...formatted]);
      if (formatted.length === 1) setSelectedLocation(formatted[0]);
    } catch (error) {
      toast.error("Error fetching filtered locations.");
    }
  };

  useEffect(() => {
    const fetchAllProducts = async () => {
      const token = sessionStorage.getItem("token");
      try {
        const response = await axios.get(`${API_BASE_URL}/products/getProductsForStockMovement`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProducts(response.data.products);
      } catch (error) {
        toast.error("Failed to fetch products.");
      }
    };
  
    fetchAllProducts();
  }, []);

  const movementNameMap = {
    production_batch_update: "Production Batch",
    production_batch: "Production Batch",
    sales_order: "Sales Order",
    transfer_orders: "Transfer Orders",
    adjustments: "Stock Adjustment",
    dispatch_stock_on_loan: "Dispatch Stock on Loan",
    purchase_order: "Purchase Order",
  };
  
  const movementTypeMap = {
    "Add": "Addition",
    "Remove": "Removal",
  }
  
  const handleDownloadExcel = async () => {
    if (movements.length === 0) {
      toast.warn("No stock movements to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Stock Movements");

    const headerRow = [
      "Source ID",
      "Source",
      "Movement Quantity",
      "New Quantity",
      "Previous Quantity",
      "Unit",
      "Performed By",
      "Location",
      "Type",
      "Date/Time",
    ];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    movements.forEach((m) => {
      worksheet.addRow([
        m.movement_code,
        m.movement_name,
        m.qty,
        m.updated_qty,
        m.previous_qty,
        m.unit,
        m.performed_by,
        m.location_name,
        m.movement_type,
        m.timestamp,
      ]);
    });

    worksheet.columns.forEach((column, i) => {
      let maxLength = headerRow[i].length;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellLength = cell.value ? cell.value.toString().length : 0;
        if (cellLength > maxLength) maxLength = cellLength;
      });
      column.width = maxLength < 20 ? 20 : maxLength;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "stock_movements.xlsx");
  };

  const fetchMovements = async () => {
    if (!selectedProduct) {
      toast.warn("Please select a product.");
      return;
    }
  
    try {
      const token = sessionStorage.getItem("token");;
  
      const response = await axios.get(
        `${API_BASE_URL}/latestStock/getStockMovementByProduct`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            product_id: selectedProduct._id,
            location_id: selectedLocation?.value || "all",
            organizationId,
          },
        }
      );
  
      const formatted = response.data.map((m, i) => ({
        id: i + 1,
        movement_type: movementTypeMap[m.movement_type] || m.movement_type,
        movement_name: movementNameMap[m.movement_name] || m.movement_name,
        qty: m.movement_qty,
        previous_qty: m.previous_qty,
        updated_qty: m.updated_stock_qty,
        updated_price: m.updated_price?.$numberDecimal || "0.00",
        unit: m.product_id?.unit?.unit_name || "N/A",
        location_name: m.location_id?.location_name || "N/A",
        timestamp: new Date(m.timestamp).toLocaleString(),
        performed_by: m.performed_by ? `${m.performed_by.username}` : "System",
        movement_code: m.movement_code || "N/A",
      }));
  
      setMovements(formatted);
    } catch (err) {
      toast.error("Failed to fetch stock movements.");
    }
  };

  const ProductOption = ({ data, innerRef, innerProps, isFocused, isSelected }) => {
    return (
      <div
        ref={innerRef}
        {...innerProps}
        className={`flex items-center p-2 ${isFocused ? "bg-gray-100" : ""} ${isSelected ? "bg-blue-600 text-white" : ""}`}
      >
        {data.product_image && (
          <img
            src={`${process.env.REACT_APP_IMAGE_BASE_URL}${data.product_image}`}
            alt={data.product_name}
            className="w-8 h-8 object-cover mr-2 rounded"
          />
        )}
        <span>{data.product_name}</span>
      </div>
    );
  };
  

  const columns = [
    { field: "id", headerName: "#", width: 70 },
    { field: "movement_code", headerName: "Source ID", flex: 1 },
    { field: "movement_name", headerName: "Source", flex: 1 },
    {
      field: "qty",
      headerName: "Movement Quantity",
      flex: 1,
      headerAlign: "right",
      align: "right",
    },
    {
      field: "updated_qty",
      headerName: "New Quantity",
      flex: 1,
      headerAlign: "right",
      align: "right",
    },
    {
      field: "previous_qty",
      headerName: "Previous Quantity",
      flex: 1,
      headerAlign: "right",
      align: "right",
    },
    { field: "unit", headerName: "Unit", flex: 1, headerAlign: "center", align: "center" },
    { field: "performed_by", headerName: "Performed By", flex: 1 },
    { field: "location_name", headerName: "Location", flex: 1 },
    {
      field: "movement_type",
      headerName: "Type",
      flex: 1,
      renderCell: (params) => {
        const type = params.row.movement_type;
        const typeClassMap = {
          "Addition": "bg-[#8BC34A] text-white",
          "Removal": "bg-[#F44336] text-white",
        };
        const typeClass = typeClassMap[type] || "bg-gray-200 text-white";
        return (
          <span className={`px-2 py-1 rounded ${typeClass}`}>
            {type}
          </span>
        );
      },
    },
    { field: "timestamp", headerName: "Date/Time", flex: 1.5 },
  ];  

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Stock Movements</h1>
        </div>

        <div className="bg-white p-4 rounded-lg shadow mb-8">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left side: selects + fetch button */}
            <div className="flex items-center gap-4 flex-wrap">
              <Select
                options={locations}
                value={selectedLocation}
                onChange={setSelectedLocation}
                placeholder="Select Location"
                className="w-64"
              />
              <Select
                options={products}
                getOptionLabel={(e) => e.product_name}
                getOptionValue={(e) => e._id}
                value={selectedProduct}
                onChange={setSelectedProduct}
                placeholder="Search and select product"
                components={{ Option: ProductOption }}
                className="w-64"
              />

              <button
                onClick={fetchMovements}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                Fetch Movements
              </button>
            </div>

            {/* Right side: download button */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>
          <DataGrid rows={movements} columns={columns} autoHeight />
        </div>
      </div>
    </div>
  );
}

export default StockMovementView;
